import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Award, FileText, Play, Stamp, Users } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { HERO } from "@/data/content";
import { scrollToHash } from "@/utils/scroll";

const BookChip = () => (
  <a
    href={HERO.youtube.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="youtube-chip"
    className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-2xl"
  >
    <div className="flex h-16 w-12 shrink-0 items-center justify-center rounded-sm bg-[#FF0000]">
      <Play size={22} className="fill-white text-white" />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-navy">
        {HERO.youtube.label}
      </p>
      <p className="truncate text-sm font-semibold text-ink">{HERO.youtube.title}</p>
      <p className="text-xs text-slate-500">{HERO.youtube.byline}</p>
    </div>
    <span className="ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy text-white">
      <ArrowUpRight size={16} />
    </span>
  </a>
);

const Portrait = ({ className = "" }) => (
  <div className={`relative h-full w-full ${className}`}>
    <img
      src="/assets/james-pratt-headshot-cutout.png"
      alt="James Pratt, founder of 1-800-Busy Dog Basement Technologies"
      className="h-full w-full object-cover object-top"
    />
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="noise relative overflow-hidden bg-navy-ink"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 75% 40%, rgba(15,61,129,0.28), transparent 70%)",
        }}
      />

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-6 pb-0 pt-36 md:px-12 lg:min-h-[92vh] lg:grid-cols-12 lg:gap-0 lg:pb-0 lg:pt-40">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7 lg:pb-24">
          <MaskedLine delay={0.05}>
            <span data-testid="hero-kicker" className="font-serif text-lg italic text-zinc-400 md:text-xl">
              {HERO.kicker}
            </span>
          </MaskedLine>

          <h1 className="mt-5 font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>
              <span>{HERO.headline}</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subhead"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 max-w-xl text-sm leading-relaxed text-zinc-400 md:text-base"
          >
            {HERO.subhead}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 pb-14 lg:pb-0"
          >
            <a
              href="#about"
              data-testid="hero-cta"
              onClick={(e) => {
                e.preventDefault();
                scrollToHash("#about");
              }}
              className="group inline-flex items-center gap-2 rounded-full bg-navy px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-navy/30 transition-colors hover:bg-navy-dark"
            >
              {HERO.ctaLabel}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
            <div data-testid="hero-badges" className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              {HERO.badges.map((badge, i) => {
                const Icon = { award: Award, file: FileText, stamp: Stamp, users: Users }[badge.icon];
                return (
                  <span
                    key={badge.text}
                    data-testid={i === 0 ? "hero-badge" : `hero-badge-${i + 1}`}
                    className="flex w-full items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-zinc-300"
                  >
                    <Icon size={14} className="shrink-0" style={{ color: "#7FA8E8" }} />
                    {badge.text}
                  </span>
                );
              })}
            </div>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5 lg:h-full">
          <motion.div
            style={{ y: portraitY }}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative h-[65vh] w-full lg:absolute lg:inset-y-0 lg:-right-12 lg:h-auto lg:w-[calc(100%+3rem)]"
          >
            <Portrait />
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="absolute bottom-8 left-4 z-10 max-w-xs lg:left-2"
            >
              <BookChip />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
